/**
 * Citations footer under an assistant answer (integration plan 05 C7,
 * 2026-09-29): the sources behind the numbers, when they were observed, and —
 * always visible, never hidden — whether coverage was partial. Parsing lives
 * in utils/toolEvidence.js; this only renders.
 */
import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { humaniseAge, formatObservedUtc } from '../../utils/toolEvidence';

function observedLabel(evidence) {
  if (evidence.kind === 'reference') return 'Reference data';
  if (evidence.kind === 'model') return 'Model output';
  const observed = formatObservedUtc(evidence.observedAt);
  if (!observed) {
    // Never invent an observation time (envelope.py also forces degraded here).
    return evidence.window ? `Over ${evidence.window}` : 'Observation time unknown';
  }
  // The absolute observation time is the truth; the relative age is only how
  // stale it was WHEN FETCHED, so it never silently ages in the transcript
  // (review C7 M2).
  const age = humaniseAge(evidence.ageSeconds);
  const fetched = age !== null ? ` · ${age} at fetch` : '';
  const win = evidence.window ? ` · over ${evidence.window}` : '';
  return `Observed ${observed}${fetched}${win}`;
}

function SourceItem({ item }) {
  return (
    <Box component="li" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      {item.url ? (
        <a href={item.url} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer">
          {item.source}
        </a>
      ) : (
        <span>{item.source}</span>
      )}
      {item.host ? (
        <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>
          ({item.host})
        </Typography>
      ) : null}
    </Box>
  );
}

/**
 * @param {{ sources: Array<{sources, extraSources, kind, observedAt, ageSeconds, window, coverage}> }} props
 */
export default function EvidenceSources({ sources }) {
  if (!Array.isArray(sources) || sources.length === 0) return null;

  return (
    <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', mb: 0.5 }}
      >
        Sources
      </Typography>
      {sources.map((evidence, i) => {
        // Restored localStorage is trusted as-is; a tampered entry must not
        // crash the thread (review C7 L1).
        if (!evidence || typeof evidence !== 'object') return null;
        const list = Array.isArray(evidence.sources) ? evidence.sources : [];
        const named = list.filter((sourceItem) => sourceItem && sourceItem.source);
        if (named.length === 0 && evidence?.coverage === 'ok') return null;
        return (
          <Box key={i} sx={{ mb: 0.75, '&:last-of-type': { mb: 0 } }}>
            <Box
              sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}
            >
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {observedLabel(evidence)}
              </Typography>
              {evidence.coverage === 'degraded' ? (
                <Chip label="Partial data" size="small" color="warning" variant="outlined" />
              ) : null}
              {evidence.coverage === 'missing' ? (
                <Chip label="No data reported" size="small" color="error" variant="outlined" />
              ) : null}
              {evidence.coverage === 'unknown' ? (
                // Fail closed: an absent/renamed coverage_status is flagged,
                // never shown as clean (review C7 M1).
                <Chip label="Coverage not reported" size="small" variant="outlined" />
              ) : null}
            </Box>
            {named.length > 0 ? (
              <Box
                component="ul"
                sx={{
                  listStyle: 'none',
                  p: 0,
                  m: 0,
                  mt: 0.25,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1.5,
                  fontSize: '0.8125rem',
                }}
              >
                {named.map((item, j) => (
                  <SourceItem key={j} item={item} />
                ))}
                {evidence.extraSources > 0 ? (
                  <Box component="li" sx={{ color: 'text.secondary' }}>
                    +{evidence.extraSources} more
                  </Box>
                ) : null}
              </Box>
            ) : null}
          </Box>
        );
      })}
    </Box>
  );
}
